import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  Search,
  Calendar,
  Clock,
  Video,
  Eye,
  RefreshCcw,
  XCircle,
  RotateCcw,
  Ban,
  X,
} from 'lucide-react'
import { interviewerApi, type ChangeRequestItem } from '../../lib/api/interviewer'
import { formatDateInTz, formatTimeInTz } from '../../lib/utils/timezone'
import { useInterviewerTimezone } from '../../hooks/useInterviewerTimezone'
import { friendlyApiError } from '../../lib/utils/apiError'

interface Session {
  id: number
  booking_id?: number | null
  title: string
  client_name: string
  scheduled_at: string
  duration_minutes: number
  status: string
  is_rescheduled?: boolean
  recording_url?: string
  has_notes?: boolean
}

export default function InterviewerSessions() {
  const interviewerTz = useInterviewerTimezone()
  const navigate = useNavigate()
  const [sessions, setSessions] = useState<Session[]>([])
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState('')

  // Change requests (interviewers cannot self-reschedule/cancel — they raise a request)
  const [changeRequests, setChangeRequests] = useState<ChangeRequestItem[]>([])
  const [crModal, setCrModal] = useState<{ session: Session; type: 'cancel' | 'reschedule' } | null>(null)
  const [crReason, setCrReason] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    fetchSessions()
    loadChangeRequests()
  }, [])

  const fetchSessions = async () => {
    setLoading(true)
    setFetchError('')
    try {
      const res = await interviewerApi.getSessions()
      setSessions(res.data || res || [])
    } catch (err) {
      setFetchError('Failed to load sessions. Please check your connection and try again.')
    } finally {
      setLoading(false)
    }
  }

  const loadChangeRequests = async () => {
    try {
      setChangeRequests(await interviewerApi.getMyChangeRequests())
    } catch {
      // Non-fatal — the request controls just won't show a pending state.
    }
  }

  // booking_id → the OPEN request on that booking, if any.
  //
  // "Open" is pending OR approved. Approval is only the admin's decision — they
  // then carry the cancel/reschedule out on the session themselves — so an
  // approved request is still in flight. Counting only `pending` here re-enabled
  // the request buttons the moment an admin approved, and the server (which
  // rejects a duplicate open request) answered the second attempt with a
  // conflict error.
  const openByBooking = new Map(
    changeRequests
      .filter((r) => r.status === 'pending' || r.status === 'approved')
      .map((r) => [r.booking_id, r] as const),
  )

  const filteredSessions = sessions.filter((session) => {
    const clientName = session.client_name || ''
    const matchesSearch = clientName.toLowerCase().includes(search.toLowerCase())
    const matchesStatus =
      statusFilter === 'all' || session.status === statusFilter
    return matchesSearch && matchesStatus
  })

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return 'badge-success'
      case 'scheduled':
        return 'badge-primary'
      case 'pending_review':
        return 'badge-warning'
      case 'cancelled':
        return 'badge-danger'
      case 'no_show':
        return 'badge-danger'
      default:
        return 'badge-primary'
    }
  }

  const formatStatus = (status: string) =>
    status
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')

  // Interviewer's timezone, matching the dashboard — not the browser's.
  const formatDate = (dateStr: string) => formatDateInTz(dateStr, interviewerTz)
  const formatTime = (dateStr: string) => formatTimeInTz(dateStr, interviewerTz)

  const openRequest = (session: Session, type: 'cancel' | 'reschedule') => {
    setCrModal({ session, type })
    setCrReason('')
  }

  const submitRequest = async () => {
    if (!crModal?.session.booking_id) return
    setSubmitting(true)
    try {
      await interviewerApi.requestBookingChange(crModal.session.booking_id, {
        type: crModal.type,
        reason: crReason.trim() || undefined,
      })
      toast.success('Change request sent — an admin will review it.')
      setCrModal(null)
      await loadChangeRequests()
    } catch (err) {
      toast.error(friendlyApiError(err, 'Failed to send change request.'))
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return <div className="loading-screen">Loading sessions...</div>
  }

  if (fetchError) {
    return (
      <div className="empty-state">
        <XCircle size={48} style={{ color: 'var(--danger)' }} />
        <h3>{fetchError}</h3>
        <button className="btn btn-secondary" onClick={fetchSessions} style={{ marginTop: 16 }}>
          <RefreshCcw size={16} /> Try Again
        </button>
      </div>
    )
  }

  return (
    <div>
      <div
        className="page-header"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div>
          <h1 className="page-title">Session Management</h1>
          <p className="page-subtitle">View and manage all interview sessions</p>
        </div>
        <button className="btn btn-secondary" onClick={() => { fetchSessions(); loadChangeRequests() }}>
          <RefreshCcw size={18} />
          Refresh
        </button>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="icon primary">
            <Calendar size={24} />
          </div>
          <div className="value">
            {sessions.filter((s) => s.status === 'scheduled').length}
          </div>
          <div className="label">Upcoming</div>
        </div>
        <div className="stat-card">
          <div className="icon success">
            <Video size={24} />
          </div>
          <div className="value">
            {sessions.filter((s) => s.status === 'completed').length}
          </div>
          <div className="label">Completed</div>
        </div>
        <div className="stat-card">
          <div className="icon warning">
            <Clock size={24} />
          </div>
          <div className="value">
            {sessions.filter((s) => s.status === 'pending_review').length}
          </div>
          <div className="label">Pending Review</div>
        </div>
      </div>

      <div className="card">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 20,
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flex: 1,
              minWidth: 200,
            }}
          >
            <Search size={18} style={{ color: 'var(--text-muted)' }} />
            <input
              type="text"
              className="form-input"
              placeholder="Search by client name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ border: 'none', boxShadow: 'none' }}
            />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {['all', 'scheduled', 'completed', 'pending_review', 'cancelled', 'no_show'].map(
              (status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  style={{
                    padding: '6px 14px',
                    border: '1px solid var(--border)',
                    background:
                      statusFilter === status ? 'var(--primary-navy)' : 'white',
                    color:
                      statusFilter === status ? 'white' : 'var(--text-muted)',
                    borderRadius: 20,
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                >
                  {status === 'all' ? 'All' : formatStatus(status)}
                </button>
              )
            )}
          </div>
        </div>

        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Client</th>
                <th>Date & Time</th>
                <th>Duration</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSessions.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    style={{
                      textAlign: 'center',
                      padding: 40,
                      color: 'var(--text-muted)',
                    }}
                  >
                    No sessions found
                  </td>
                </tr>
              ) : (
                filteredSessions.map((session) => {
                  const canRequest = !!session.booking_id && session.status === 'scheduled'
                  const openReq = session.booking_id ? openByBooking.get(session.booking_id) : undefined
                  return (
                    <tr key={session.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div className="avatar">
                            {(session.client_name || 'C').charAt(0)}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600 }}>
                              {session.client_name || 'Unknown Client'}
                            </div>
                            <div
                              style={{ fontSize: 12, color: 'var(--text-muted)' }}
                            >
                              {session.title}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 4,
                          }}
                        >
                          <span
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                              fontSize: 13,
                            }}
                          >
                            <Calendar size={14} />
                            {formatDate(session.scheduled_at)}
                          </span>
                          <span
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                              fontSize: 13,
                              color: 'var(--text-muted)',
                            }}
                          >
                            <Clock size={14} />
                            {formatTime(session.scheduled_at)}
                          </span>
                        </div>
                      </td>
                      <td>{session.duration_minutes || 60} min</td>
                      <td>
                        <span className={`badge ${getStatusBadge(session.status)}`}>
                          {session.is_rescheduled && (
                            <RefreshCcw
                              size={12}
                              style={{ marginRight: 4 }}
                            />
                          )}
                          {formatStatus(session.status)}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <button
                            className="btn btn-secondary"
                            style={{ padding: '6px 14px', fontSize: 13 }}
                            onClick={() =>
                              navigate(`/interviewer/sessions/${session.id}`)
                            }
                          >
                            <Eye size={14} />
                            View
                          </button>
                          {openReq ? (
                            /* Two distinct states, because they mean different
                               things to the interviewer: still being reviewed, vs
                               agreed but not carried out yet. Either way the
                               session remains theirs until it actually changes. */
                            <span
                              className={`badge ${openReq.status === 'approved' ? 'badge-primary' : 'badge-warning'}`}
                              title={
                                openReq.status === 'approved'
                                  ? 'Approved by an admin. The session stays on your schedule until they apply the change.'
                                  : 'Change requested — an admin is reviewing it. The session is unchanged until they approve.'
                              }
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}
                            >
                              <Clock size={12} />
                              {openReq.status === 'approved' ? 'Approved — being applied' : 'Awaiting admin review'}
                            </span>
                          ) : canRequest ? (
                            <>
                              <button
                                className="btn btn-secondary"
                                style={{ padding: '6px 12px', fontSize: 13 }}
                                title="Request a reschedule (admin approval required)"
                                onClick={() => openRequest(session, 'reschedule')}
                              >
                                <RotateCcw size={14} />
                                Request Reschedule
                              </button>
                              <button
                                className="btn btn-secondary"
                                style={{ padding: '6px 12px', fontSize: 13, color: 'var(--danger)' }}
                                title="Request a cancellation (admin approval required)"
                                onClick={() => openRequest(session, 'cancel')}
                              >
                                <Ban size={14} />
                                Request Cancel
                              </button>
                            </>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Change-request modal — interviewers cannot self-reschedule/cancel */}
      {crModal && (
        <div className="modal-overlay" onClick={() => !submitting && setCrModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h3 className="modal-title">
                {crModal.type === 'reschedule' ? 'Request Reschedule' : 'Request Cancellation'}
              </h3>
              <button onClick={() => !submitting && setCrModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.6 }}>
                This raises a request for an admin to review. The session with{' '}
                <strong>{crModal.session.client_name || 'the client'}</strong> is not changed until an admin approves it.
              </p>
              <div className="form-group" style={{ marginTop: 12 }}>
                <label className="form-label">Reason {crModal.type === 'cancel' ? '' : '(optional)'}</label>
                <textarea
                  className="form-input"
                  rows={4}
                  value={crReason}
                  onChange={(e) => setCrReason(e.target.value)}
                  placeholder={crModal.type === 'reschedule' ? 'Why do you need to reschedule?' : 'Why do you need to cancel?'}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setCrModal(null)} disabled={submitting}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={submitRequest} disabled={submitting}>
                {submitting ? 'Sending...' : 'Send Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
